import type { RefObject } from 'react'

import { useEffect, useRef } from 'react'
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { usePageTransition } from '@/components/motion/transition-context'

/**
 * Scroll reveals driven by data attributes, so any element in a page can opt in:
 *   data-reveal="fade" | "up" | "words" | "chars" | "image" | "line"
 *   data-reveal-delay="0.2" (seconds, optional)
 * "words" and "chars" expect the markup produced by <SplitWords> / <SplitChars>.
 */
const maskTargets = (element: HTMLElement): Array<Element> => Array.from(element.querySelectorAll('.mask > span'))

const hide = (element: HTMLElement): void => {
    switch (element.dataset.reveal) {
        case 'words':
        case 'chars':
            gsap.set(maskTargets(element), { yPercent: 140 })
            break
        case 'image':
            gsap.set(element, { clipPath: 'inset(100% 0% 0% 0%)' })
            gsap.set(element.querySelectorAll('img'), { scale: 1.3 })
            break
        case 'line':
            gsap.set(element, { scaleX: 0, transformOrigin: '0% 50%' })
            break
        case 'up':
            gsap.set(element, { autoAlpha: 0, y: 70 })
            break
        default:
            gsap.set(element, { autoAlpha: 0, y: 28, filter: 'blur(8px)' })
    }
}

export const playReveal = (element: HTMLElement, delay = 0): void => {
    const start = delay + (Number(element.dataset.revealDelay) || 0)

    switch (element.dataset.reveal) {
        case 'words':
            gsap.to(maskTargets(element), { yPercent: 0, duration: 1.15, stagger: 0.035, delay: start, ease: 'expo.out' })
            break
        case 'chars':
            gsap.to(maskTargets(element), { yPercent: 0, duration: 1.2, stagger: 0.028, delay: start, ease: 'expo.out' })
            break
        case 'image':
            gsap.to(element, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, delay: start, ease: 'expo.inOut' })
            gsap.to(element.querySelectorAll('img'), { scale: 1, duration: 1.9, delay: start, ease: 'expo.out' })
            break
        case 'line':
            gsap.to(element, { scaleX: 1, duration: 1.3, delay: start, ease: 'expo.inOut' })
            break
        case 'up':
            gsap.to(element, { autoAlpha: 1, y: 0, duration: 1.2, delay: start, ease: 'expo.out' })
            break
        default:
            gsap.to(element, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 1.1, delay: start, ease: 'expo.out', clearProps: 'filter' })
    }
}

export const useReveals = (scope: RefObject<HTMLElement | null>): void => {
    const { ready } = usePageTransition()

    useGSAP(() => {
        if (prefersReducedMotion() || !scope.current) return

        scope.current.querySelectorAll<HTMLElement>('[data-reveal]').forEach(hide)
    }, { scope })

    useEffect(() => {
        const root = scope.current

        if (!ready || !root || prefersReducedMotion()) return

        const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))
        const viewport = window.innerHeight
        const triggers: Array<ScrollTrigger> = []
        let queue: Array<HTMLElement> = []
        let timer: number | null = null
        let first = true

        const flush = (): void => {
            const base = first ? 0.2 : 0

            queue.forEach((element, index) => playReveal(element, base + index * 0.08))
            queue = []
            timer = null
            first = false
        }

        const enqueue = (element: HTMLElement): void => {
            queue.push(element)

            if (timer === null) timer = window.setTimeout(flush, 40)
        }

        elements.forEach(element => {
            const rect = element.getBoundingClientRect()

            if (rect.top < viewport * 0.94 && rect.bottom > 0) {
                enqueue(element)

                return
            }

            triggers.push(ScrollTrigger.create({
                trigger: element,
                once: true,
                start: () => {
                    const top = element.getBoundingClientRect().top + window.scrollY
                    const max = ScrollTrigger.maxScroll(window)

                    return Math.max(1, Math.min(top - window.innerHeight * 0.9, max - 2))
                },
                onEnter: () => enqueue(element)
            }))
        })

        return () => {
            triggers.forEach(trigger => trigger.kill())

            if (timer !== null) window.clearTimeout(timer)
        }
    }, [ready, scope])
}

/**
 * A paused timeline built at mount (so its "from" states hide content before paint)
 * that plays once the page transition hands over.
 */
export const useEntrance = (
    scope: RefObject<HTMLElement | null>,
    build: (timeline: gsap.core.Timeline) => void
): void => {
    const { ready, enterDelay } = usePageTransition()
    const timeline = useRef<gsap.core.Timeline | null>(null)

    useGSAP(() => {
        if (prefersReducedMotion()) return

        const entrance = gsap.timeline({ paused: true })

        build(entrance)
        timeline.current = entrance
    }, { scope })

    useEffect(() => {
        if (!ready || !timeline.current) return

        const call = gsap.delayedCall(enterDelay, () => {
            timeline.current?.play()
        })

        return () => {
            call.kill()
        }
    }, [ready, enterDelay])
}
